import { Button } from '@app/components';
import { ContentLoader } from '@app/components/loaders/content-loader/ContentLoader';
import { useGetPlans } from '@app/domain/plan/api/plan.hooks.api';
import { useActivePlan } from '@app/domain/plan/hooks/useActivePlan';
import { SubscriptionApi } from '@app/domain/subscription/api/subscription.api';
import { ConfirmDialog } from '@app/hoc/confirm-dialog/ConfirmDialog';
import { ECurrency, EPlanCycle, EPlanStatus } from '@app/swagger-types';
import { clsxm } from '@app/styles/clsxm';
import { Box, FormControlLabel, Switch } from '@mui/material';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { SubscriptionPlanContainer } from './SubscriptionPlanContainer';
import { capitalize } from 'lodash';
import { useAuthorizedUser } from '@app/auth/useAuthorizedUser';
import { CurrencySelector } from '@app/domain/subscription/components/CurrencySelector';
import { useCancelSubscriptionPlan } from '@app/domain/subscription/hooks/useCancelSubscriptionPlan';
import { TrialProgressBanner } from './TrialProgressBanner';
import { useRenewSubscription } from '@app/domain/subscription/hooks/useRenewSubscription';

const SHOULD_SHOW_CYCLE_SWITCH = false;

export const SubscriptionPlans: React.FC = () => {
  const { t } = useTranslation();
  const [isShowAllFeatures, setIsShowAllFeatures] = useState(false);
  const { isOldPlanUser, is5GenUser, currentUser } = useAuthorizedUser();

  const {
    isTrial,
    isExpired,
    planStatus,
    plan,
    isFreePlan,
    // planCycle: userCycle, // use cycle if we would have more cycles to select again
    scheduledUpdate,
    friendlyScheduledDate,
    daysToExpire,
    isTrialExpired: isNewGenUserTrialExpired,
    isPlanCanceledStillActive,
  } = useActivePlan();

  const isTrialExpired = isTrial && isExpired;

  // userCycle not needed anymore
  const [planCycle, setPlanCycle] = useState(EPlanCycle.YEARLY);
  const [selectedCurrency, setSelectedCurrency] = useState(plan?.price?.planCurrency || ECurrency.EUR);
  const { data: plans, isFetching } = useGetPlans({ currency: selectedCurrency });
  const { handleNavigateToCancelForm } = useCancelSubscriptionPlan();
  const renewSubscriptionMutation = useRenewSubscription();

  useEffect(() => {
    if (isTrialExpired) {
      setIsShowAllFeatures(true);
    }
  }, [isTrialExpired]);

  const cancelScheduledUpdateMutation = useMutation(
    async () => {
      return await SubscriptionApi.cancelScheduledUpdate();
    },
    {
      onSuccess: () => {
        window.location.reload();
      },
    }
  );
  const [, setAutoOpenKeyForCancelPlanConfirmation] = useState(0);
  const askToCancelPlan = () => {
    setAutoOpenKeyForCancelPlanConfirmation((i) => i + 1);
  };

  const shouldShowCancelPlanButton = !scheduledUpdate && planStatus === EPlanStatus.ACTIVE && !isFreePlan;
  const scheduledPlanName = scheduledUpdate ? capitalize(scheduledUpdate.planType) : '-';
  const scheduledPlanCycle = scheduledUpdate
    ? scheduledUpdate.planCycle === EPlanCycle.MONTHLY
      ? t('subscription.monthly')
      : scheduledUpdate.planCycle === EPlanCycle.QUARTERLY
      ? t('subscription.quarterly')
      : t('subscription.yearly')
    : '-';

  const renderCycleSwitch = () => {
    if (!SHOULD_SHOW_CYCLE_SWITCH) {
      return null;
    }
    const FASTER_CYCLE = isOldPlanUser || is5GenUser ? EPlanCycle.MONTHLY : EPlanCycle.QUARTERLY;

    return (
      <div className="flex flex-wrap items-center">
        <Box
          onClick={() => {
            setPlanCycle(() => FASTER_CYCLE);
          }}
          className={clsxm(
            'mr-5 whitespace-nowrap text-gray-500 hover:cursor-pointer',
            planCycle === FASTER_CYCLE && 'text-accept-dark-blue'
          )}
        >
          {isOldPlanUser || is5GenUser ? t('subscription.billMonthly') : t('subscription.billQuarterly')}
        </Box>
        <FormControlLabel
          onChange={() => {
            setPlanCycle((prev) => (prev === EPlanCycle.YEARLY ? FASTER_CYCLE : EPlanCycle.YEARLY));
          }}
          checked={planCycle === EPlanCycle.YEARLY}
          control={<Switch />}
          className="mr-0"
          label=""
        />
        <Box
          onClick={() => {
            setPlanCycle(() => EPlanCycle.YEARLY);
          }}
          className={clsxm(
            'ml-2 whitespace-nowrap text-gray-600 hover:cursor-pointer',
            planCycle === EPlanCycle.YEARLY && 'text-accept-dark-blue'
          )}
        >
          {t('subscription.billYearly')}
        </Box>
        {/* TODO: set right value of discount */}
        <div className="ml-4 rounded-lg bg-accept-light px-1 font-medium text-accept-main">-33%</div>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col items-start justify-between gap-4 px-6 lg:flex-row lg:px-0">
        <div className="flex">
          {isNewGenUserTrialExpired ? (
            <div className="flex flex-col gap-4">
              <div className="mt-3 py-[2px] flex items-center gap-2 text-xl font-bold">
                <span>{t('subscription.yourTrialHasEnded')}</span>
              </div>
              <div>
                <Trans i18nKey={'subscription.choosePlanToContinue'} className="text-s">
                  <b />
                </Trans>
              </div>
            </div>
          ) : currentUser?.plan ? (
            <div className="mt-3 py-[2px] flex items-center gap-2 text-xl font-bold">
              <span>
                {t('subscription.currentPlan')}: {currentUser?.plan?.name}
              </span>
              {daysToExpire !== undefined && daysToExpire >= 0 && isTrial && (
                <span className="rounded-lg bg-gray-200 px-3 py-[2px] font-bold text-gray-800 text-xs">
                  {t('subscription.expiresInDays', { days: daysToExpire })}
                </span>
              )}
            </div>
          ) : (
            <div className="py-[2px] font-bold text-gray-800 lg:text-xxxl">{t('subscription.selectAPlan')}</div>
          )}
        </div>

        {shouldShowCancelPlanButton && !isTrial && (
          <Button variant="outlined" className="bg-transparent" onClick={handleNavigateToCancelForm}>
            {t('subscription.cancelPlan')}
          </Button>
        )}
        {isPlanCanceledStillActive && (
          <Button
            variant="contained"
            onClick={() => renewSubscriptionMutation.mutate()}
            loading={renewSubscriptionMutation.isLoading}
          >
            {t('subscription.renewPlan')}
          </Button>
        )}
        {scheduledUpdate && (
          <ConfirmDialog
            trigger={
              <Button variant="outlined" className="bg-transparent" loading={cancelScheduledUpdateMutation.isLoading}>
                {t('subscription.cancelUpdate')}
              </Button>
            }
            containerClassName="max-w-[350px]"
            onConfirm={cancelScheduledUpdateMutation.mutate}
            textClassName="text-center"
            title={t('subscription.areYouSureToCancelScheduledUpdate', { planName: scheduledPlanName })}
            confirmText={t('dialog.confirm')}
          />
        )}
      </div>
      {isTrial && !isTrialExpired && !isNewGenUserTrialExpired && <TrialProgressBanner />}
      {scheduledUpdate && (
        <div className="rounded bg-gray-200 p-3">
          {t('subscription.planUpdateScheduled', {
            planName: scheduledPlanName,
            planCycle: scheduledPlanCycle,
            scheduledDate: friendlyScheduledDate,
          })}
        </div>
      )}
      <div className="flex flex-col gap-4 px-6 lg:flex-row lg:items-center lg:justify-between lg:px-0">
        <div className="flex flex-col gap-4 w-full justify-between lg:flex-row lg:items-center">
          {renderCycleSwitch()}

          <div className="flex">
            <CurrencySelector value={selectedCurrency} onChange={(v) => setSelectedCurrency(v)} />
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-4 px-6 lg:px-0 xl:flex-row">
        {!plans && isFetching ? <ContentLoader loading entity="Plans" isEmpty={false} /> : null}
        {plans?.result.map((plan, index) => (
          <SubscriptionPlanContainer
            key={`${plan.id}_${index}`}
            plan={plan}
            selectedPlanCycle={planCycle}
            selectedCurrency={selectedCurrency}
            isShowAllFeatures={isShowAllFeatures}
            setIsShowAllFeatures={setIsShowAllFeatures}
            askToDowngradeToFreePlan={askToCancelPlan}
            shouldAllowDowngradeToFreePlan={shouldShowCancelPlanButton}
          />
        ))}
      </div>
    </div>
  );
};
